import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, FileSignature, CheckCircle2, AlertTriangle, Minus, Download } from "lucide-react";

export default function GenerateContractDialog({ submission, open, onOpenChange }) {
    const [templates, setTemplates] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");
    const [category, setCategory] = useState("");
    const [picked, setPicked] = useState(null);
    const [generating, setGenerating] = useState(false);

    useEffect(() => {
        if (!open) return;
        (async () => {
            setLoading(true);
            try {
                // Task 27 Bug-3 — narrow to contract-type templates only
                // (this dialog is for generating rental contracts from a
                // submission; ACH / invoice / COI templates don't belong
                // in this picker).
                const { data } = await api.get("/admin/templates", { params: { template_type: "contract", archived: false } });
                setTemplates(data.items || []);
                setCategories(data.categories || []);
                setPicked(null);
                setQ("");
                setCategory("");
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
    }, [open]);

    const filledKeys = useMemo(() => {
        const data = submission?.data || {};
        const out = new Set();
        for (const [k, v] of Object.entries(data)) {
            if (v == null || v === "") continue;
            if (typeof v === "object") {
                for (const [sk, sv] of Object.entries(v)) {
                    if (sv != null && sv !== "") out.add(`${k}.${sk}`);
                }
            } else {
                out.add(k);
            }
        }
        for (const u of submission?.uploads || []) out.add(u.field_key);
        for (const s of submission?.signatures || []) out.add(s.field_key);
        out.add("submission_id");
        out.add("today");
        out.add("customer_name");
        out.add("customer_email");
        if (submission?.submitted_at) out.add("submitted_at");
        return out;
    }, [submission]);

    const filtered = useMemo(() => {
        const lc = q.toLowerCase();
        return templates.filter((t) => {
            if (category && t.category !== category) return false;
            if (lc && !(t.name.toLowerCase().includes(lc) || (t.description || "").toLowerCase().includes(lc))) return false;
            return true;
        });
    }, [templates, q, category]);

    const generate = async () => {
        if (!picked) return;
        setGenerating(true);
        try {
            const resp = await api.post(
                `/admin/submissions/${submission.id}/generate-contract`,
                { template_id: picked.id },
                { responseType: "blob" },
            );
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            const safe = picked.name.replace(/[^A-Za-z0-9._-]+/g, "_").replace(/^_+|_+$/g, "") || "contract";
            a.download = `${safe}-${submission.id}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Contract generated");
            onOpenChange(false);
        } catch (err) {
            // err.response.data is a Blob — try to read as text
            try {
                const txt = await err?.response?.data?.text?.();
                if (txt) {
                    const j = JSON.parse(txt);
                    toast.error(j.detail || "Generation failed");
                } else {
                    toast.error(formatApiError(err));
                }
            } catch {
                toast.error(formatApiError(err));
            }
        } finally {
            setGenerating(false);
        }
    };

    const tagBadge = (tag, isUnknown) => {
        const present = filledKeys.has(tag);
        if (isUnknown) {
            return (
                <span key={tag} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-100 font-mono" title="Unknown tag">
                    <AlertTriangle size={10} /> {tag}
                </span>
            );
        }
        return (
            <span
                key={tag}
                className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded font-mono border ${
                    present
                        ? "bg-emerald-50 text-emerald-900 border-emerald-100"
                        : "bg-slate-50 text-slate-500 border-slate-200"
                }`}
                title={present ? "Submission has this value" : "No value in submission"}
            >
                {present ? <CheckCircle2 size={10} /> : <Minus size={10} />} {tag}
            </span>
        );
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="generate-contract-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <FileSignature size={18} /> Generate contract
                    </DialogTitle>
                    <DialogDescription>
                        Pick a template. We'll fill its merge tags from this submission and download the resulting PDF.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col sm:flex-row gap-2 mt-2">
                    <div className="relative flex-1">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            data-testid="contract-template-search"
                            placeholder="Search templates…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="pl-8"
                        />
                    </div>
                    <select
                        data-testid="contract-template-category"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="h-10 rounded-md border border-slate-200 bg-white text-sm px-3"
                    >
                        <option value="">All categories</option>
                        {categories.map((c) => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>
                </div>

                <div className="mt-3 max-h-[50vh] overflow-y-auto space-y-2 pr-1" data-testid="contract-template-list">
                    {loading && <p className="text-sm text-slate-500">Loading templates…</p>}
                    {!loading && filtered.length === 0 && (
                        <p className="text-sm text-slate-500 text-center py-8">No templates match.</p>
                    )}
                    {!loading && filtered.map((t) => {
                        const selected = picked?.id === t.id;
                        return (
                            <button
                                type="button"
                                key={t.id}
                                onClick={() => setPicked(t)}
                                data-testid={`contract-template-row-${t.id}`}
                                className={`w-full text-left rounded-md border p-3 transition-colors ${
                                    selected ? "border-blue-900 bg-blue-50/40" : "border-slate-200 hover:bg-slate-50"
                                }`}
                            >
                                <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="font-medium text-slate-900">{t.name}</div>
                                        <div className="text-xs text-slate-500">
                                            {t.source_type} {t.category ? `· ${t.category}` : ""}
                                        </div>
                                    </div>
                                    {selected && <CheckCircle2 size={18} className="text-blue-900 shrink-0" />}
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1">
                                    {t.detected_tags.map((tag) =>
                                        tagBadge(tag, t.unknown_tags.includes(tag))
                                    )}
                                    {t.detected_tags.length === 0 && (
                                        <span className="text-xs text-slate-400">No merge tags in this template.</span>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>

                <DialogFooter className="mt-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="contract-dialog-cancel">Cancel</Button>
                    <Button
                        onClick={generate}
                        disabled={!picked || generating}
                        data-testid="contract-generate-button"
                        className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                    >
                        <Download size={14} /> {generating ? "Generating…" : "Generate PDF"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
